/**
 * 텍스트형 PPT 한 장 안의 요소 옮기기 — iframe 문서(1280×720 좌표)를 직접 다룬다.
 * 옮기기는 translate, 앞뒤 순서는 z-index 를 요소 style 속성에 적어 저장 경로(sanitize_fragment)를 그대로 쓴다.
 * transform 대신 translate 를 쓰는 건 슬라이드 CSS 의 회전·기울임을 덮어쓰지 않으려는 것.
 */

export const PICK_ATTR = 'data-wqfd-pick';
export const ARRANGE_STYLE_ID = 'wqfd-arrange-style';
export const ARRANGE_CSS = `
[data-slide-root],[data-slide-root] *{cursor:default!important;user-select:none;-webkit-user-select:none}
[${PICK_ATTR}]{outline:3px solid #3182F6!important;outline-offset:3px;cursor:move!important}
[data-slide-root] [data-wqfd-edit],[data-slide-root] [data-wqfd-edit] *{cursor:text!important;user-select:text;-webkit-user-select:text}
[data-wqfd-edit]{outline:3px solid #3182F6!important;outline-offset:3px}
`;

const FULL_RATIO = 0.9;

const hasOwnText = (el) => Array.from(el.childNodes).some(
  (node) => node.nodeType === 3 && node.textContent.trim().length > 0,
);

const hasVisibleBox = (el) => {
  const win = el.ownerDocument?.defaultView;
  if (!win?.getComputedStyle) return false;
  const cs = win.getComputedStyle(el);
  const bg = cs.backgroundColor || '';
  const clear = !bg || bg === 'transparent' || /rgba\([^)]*,\s*0(?:\.0+)?\)$/.test(bg);
  const border = ['Top', 'Right', 'Bottom', 'Left'].some((side) => parseFloat(cs[`border${side}Width`]) > 0
    && cs[`border${side}Style`] !== 'none');
  return !clear || (cs.backgroundImage && cs.backgroundImage !== 'none') || border
    || (cs.boxShadow && cs.boxShadow !== 'none');
};

const tagOf = (el) => String(el?.tagName || '').toLowerCase();

const isGraphic = (el) => tagOf(el) === 'svg' || el.hasAttribute('data-icon') || tagOf(el) === 'hr';

/** 누른 곳에서 옮길 요소: 아이콘·그림은 통째로, 글자는 그 글 상자, 아니면 색·테두리가 보이는 상자. 슬라이드 전체를 덮는 배경은 고르지 않는다. */
const isFullSlide = (el, root) => {
  const box = root.getBoundingClientRect();
  if (!box.width || !box.height) return false;
  const r = el.getBoundingClientRect();
  return r.width >= box.width * FULL_RATIO && r.height >= box.height * FULL_RATIO;
};

const isPickable = (el) => isGraphic(el) || hasOwnText(el) || hasVisibleBox(el);

const climb = (start, root) => {
  for (let cur = start; cur && cur !== root; cur = cur.parentElement) {
    if (!root.contains(cur)) return null;
    if (isFullSlide(cur, root)) return null;
    if (isPickable(cur)) return cur;
  }
  return null;
};

export const pickArrangeTarget = (target, root) => {
  if (!root) return null;
  let el = target?.nodeType === 1 ? target : target?.parentElement;
  if (!el || el === root || !root.contains(el)) return null;
  const icon = el.closest('[data-icon]');
  if (icon && root.contains(icon)) el = icon;
  else {
    let svg = null;
    for (let cur = el; cur && cur !== root; cur = cur.parentElement) if (tagOf(cur) === 'svg') svg = cur;
    if (svg) el = svg;
  }
  return climb(el, root);
};

/** 고른 요소를 감싼 한 단계 바깥 묶음 (카드 안 글자 → 카드). */
export const parentArrangeTarget = (el, root) => (el && el.parentElement && el.parentElement !== root
  ? climb(el.parentElement, root)
  : null);

/**
 * 누른 곳을 감싼 고를 수 있는 요소들 — 안쪽부터 바깥쪽 순.
 * 글 상자 안의 굵은 글씨 같은 조각은 따로 고르지 않고 그 글 상자부터 센다.
 */
export const arrangeChain = (target, root, textBoxes = []) => {
  let start = pickArrangeTarget(target, root);
  if (!start) return [];
  const box = textBoxes.find((t) => t !== start && t.contains(start));
  if (box) start = box;
  const chain = [start];
  for (let cur = parentArrangeTarget(start, root); cur; cur = parentArrangeTarget(cur, root)) chain.push(cur);
  return chain;
};

// jsdom·구형 브라우저가 모르는 CSS 속성도 남도록 style 속성 글자를 직접 고친다.
const readStyleProp = (el, name) => {
  const text = el.getAttribute('style') || '';
  const m = text.match(new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`, 'i'));
  return m ? m[1].trim() : '';
};

const writeStyleProp = (el, name, value) => {
  const text = el.getAttribute('style') || '';
  const parts = text.split(';').map((p) => p.trim()).filter((p) => p && !new RegExp(`^${name}\\s*:`, 'i').test(p));
  if (value !== null && value !== '') parts.push(`${name}: ${value}`);
  if (parts.length) el.setAttribute('style', `${parts.join('; ')};`);
  else el.removeAttribute('style');
};

export const readTranslate = (el) => {
  const raw = readStyleProp(el, 'translate');
  const nums = raw.match(/-?\d+(?:\.\d+)?/g) || [];
  return { x: Number(nums[0]) || 0, y: Number(nums[1]) || 0 };
};

export const setTranslate = (el, x, y) => {
  const rx = Math.round(x);
  const ry = Math.round(y);
  writeStyleProp(el, 'translate', rx || ry ? `${rx}px ${ry}px` : null);
};

const zOf = (el) => {
  const own = parseInt(readStyleProp(el, 'z-index'), 10);
  if (Number.isFinite(own)) return own;
  const computed = parseInt(el.ownerDocument?.defaultView?.getComputedStyle?.(el)?.zIndex, 10);
  return Number.isFinite(computed) ? computed : 0;
};

/** 같은 묶음 안 형제 요소들보다 위(+1)·아래(-1)로. z-index 는 위치가 정해진 요소에만 먹어서 static 이면 relative 로 둔다. */
export const shiftLayer = (el, direction) => {
  const siblings = Array.from(el.parentElement?.children || []).filter((s) => s !== el);
  const zs = siblings.map(zOf);
  const next = direction > 0 ? Math.max(0, ...zs) + 1 : Math.min(0, ...zs) - 1;
  const position = el.ownerDocument?.defaultView?.getComputedStyle?.(el)?.position || '';
  if ((!position || position === 'static') && !readStyleProp(el, 'position')) writeStyleProp(el, 'position', 'relative');
  writeStyleProp(el, 'z-index', String(next));
  return next;
};

export const clearPick = (root) => {
  root?.querySelectorAll?.(`[${PICK_ATTR}]`).forEach((el) => el.removeAttribute(PICK_ATTR));
};

export const markPick = (root, el) => {
  clearPick(root);
  if (el) el.setAttribute(PICK_ATTR, '');
};
